import React, { useState } from 'react';
import axios from 'axios';
import { GuideLink } from '../common';
import { ROLE_GUIDES } from '../../utils';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * "Approved, pending fixes" — the last step before anything ships (Q3,
 * 2026-08-24).
 *
 * The cycle is DECIDED. No further review is coming. What is left is the
 * agreed fixes: CellcoHub wrote in the ones that carried replacement text, and
 * anything else is sitting in the working file as an anchored comment for a
 * human. Pressing Confirm is what archives and distributes the content.
 *
 * WHERE THIS MOUNTS MATTERS. It goes OUTSIDE the `!readOnlyParticipant` block
 * in ReviewInterface. The producer — the person who will normally press this —
 * is a read-only participant, and the two controls that were previously put
 * inside that block ("Mark ready to distribute" and DoneIteratingButton) both
 * turned out to be unreachable by exactly the people they were built for. The
 * access grant is what hides the control; see the `access-grant-hides-the-
 * control` note. This panel takes `canConfirm` as a prop and renders a
 * read-only state for everyone else, rather than being gated by its mount.
 */
interface Props {
  cycleId: string;
  /** cycle.amendment — the change spec record, for the counts. */
  amendment?: any;
  /** Producer, decision maker or manage_submissions. The backend re-checks. */
  canConfirm: boolean;
  /** Re-fetch the cycle. REQUIRED: the panel disappears once confirmed, and a
   *  stale view would leave a button that 409s on the second press. */
  onConfirmed: () => void;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

const ConfirmFixesPanel: React.FC<Props> = ({ cycleId, amendment, canConfirm, onConfirmed, getAuthHeaders }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);

  const applied = amendment?.appliedCopy || {};
  const wroteIntoFile = !!applied.wroteOriginal;
  const appliedCount = applied.applied?.length || 0;
  const total = amendment?.itemCount || 0;
  // What still needs a person. Mirrors the backend's own arithmetic so the
  // number on screen is the number the route will refuse on.
  const todo = wroteIntoFile
    ? (applied.annotated?.length || 0) + (applied.unanchored?.length || 0)
    : total;
  const fileLink = applied.webUrl || null;
  const auditLink = applied.auditCopy && !applied.auditCopy.skipped ? applied.auditCopy.webUrl : null;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${backendUrl}/api/content-review/cycles/${cycleId}/confirm-fixes`, {}, { headers });
      onConfirmed();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(data?.error || 'Could not confirm the fixes.');
      if (typeof data?.remaining === 'number') setRemaining(data.remaining);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="review-section"
      data-testid="confirm-fixes-panel"
      style={{ borderLeft: '4px solid #2e7d32', background: '#f6fbf7' }}
    >
      <h3 style={{ marginTop: 0 }}>Approved — fixes to complete <GuideLink href={ROLE_GUIDES.producerComments} label="What the file comments mean" /></h3>
      <p style={{ fontSize: 13, color: '#555', margin: '0 0 12px' }}>
        This content is <strong>approved</strong>. It has not been published yet — the agreed fixes
        are completed first, then confirmed here. No further review round is needed.
      </p>

      <div style={{ fontSize: 13, color: '#333', margin: '0 0 12px' }}>
        {wroteIntoFile ? (
          <>
            <div>
              CellcoHub applied <strong>{appliedCount} of {total}</strong>{' '}
              {total === 1 ? 'fix' : 'fixes'} directly into the working file.
            </div>
            {todo > 0 ? (
              <div style={{ marginTop: 4 }}>
                <strong>{todo}</strong> {todo === 1 ? 'finding' : 'findings'} could not be written in
                automatically and {todo === 1 ? 'is' : 'are'} left as anchored comments in the file
                for you to handle.
              </div>
            ) : (
              <div style={{ marginTop: 4 }}>
                Nothing is left to change. Open the file, check it reads correctly, then confirm.
              </div>
            )}
          </>
        ) : (
          <div>
            Nothing could be written into a file for this content type, so all{' '}
            <strong>{total}</strong> agreed {total === 1 ? 'fix' : 'fixes'} are yours to make at the
            destination. Confirming records your statement that they were made — it is not a check
            the system can perform.
          </div>
        )}
      </div>

      {(fileLink || auditLink) && (
        <div style={{ fontSize: 13, margin: '0 0 12px' }}>
          {fileLink && (
            <div>
              <a href={fileLink} target="_blank" rel="noopener noreferrer">Open the working file</a>
            </div>
          )}
          {auditLink && (
            <div style={{ marginTop: 2 }}>
              <a href={auditLink} target="_blank" rel="noopener noreferrer">
                Open the tracked-changes copy
              </a>
              <span style={{ color: '#888' }}> — shows each edit as a Word revision</span>
            </div>
          )}
        </div>
      )}

      {applied.skipped && applied.reason && (
        <div style={{ fontSize: 12, color: '#8a6d3b', background: '#fcf8e3', padding: 8, borderRadius: 4, marginBottom: 12 }}>
          CellcoHub did not write into the file: {applied.reason}
        </div>
      )}

      {error && (
        <div style={{ fontSize: 13, color: '#a94442', background: '#f2dede', padding: 10, borderRadius: 4, marginBottom: 12 }}>
          {error}
          {remaining !== null && remaining > 0 && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              The file has not changed since CellcoHub applied its fixes.
            </div>
          )}
        </div>
      )}

      {canConfirm ? (
        <button className="btn-primary" onClick={confirm} disabled={busy} data-testid="confirm-fixes-button">
          {busy ? 'Confirming…' : 'Confirm fixes complete'}
        </button>
      ) : (
        <div style={{ fontSize: 12, color: '#888' }}>
          The content producer or a decision maker confirms this.
        </div>
      )}
      <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
        Confirming archives and distributes this content. Nothing ships before then.
      </div>
    </div>
  );
};

export default ConfirmFixesPanel;
